import { getSupabase } from './supabase';

export interface ChatConversation {
  id: string;
  user_id: string;
  title: string | null;
  conversation_type: 'general' | 'featured' | 'quest' | 'daily_checkin';
  source_id: string | null;
  metadata: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface ChatMessage {
  id: string;
  conversation_id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  created_at: string;
}

export interface DailyCheckin {
  id: string;
  user_id: string;
  feeling_level: number | null;
  note: string | null;
  current_focus: string | null;
  conversation_id: string | null;
  created_at: string;
}

/**
 * Check if user has checked in today
 */
export async function hasCheckedInToday(userId: string): Promise<boolean> {
  const supabase = getSupabase();
  const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format

  const { data, error } = await supabase
    .from('daily_checkins')
    .select('id')
    .eq('user_id', userId)
    .gte('created_at', `${today}T00:00:00`)
    .lte('created_at', `${today}T23:59:59`)
    .maybeSingle();

  if (error) {
    console.error('Error checking daily check-in:', error);
    return false;
  }

  return data !== null;
}

/**
 * Create a new chat conversation
 */
export async function createConversation(
  userId: string,
  type: 'general' | 'featured' | 'quest' | 'daily_checkin',
  options: {
    title?: string;
    sourceId?: string;
    metadata?: Record<string, any>;
  } = {}
): Promise<ChatConversation | null> {
  const supabase = getSupabase();

  const { data, error } = await supabase
    .from('chat_conversations')
    .insert({
      user_id: userId,
      conversation_type: type,
      title: options.title || null,
      source_id: options.sourceId || null,
      metadata: options.metadata || {},
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating conversation:', error);
    return null;
  }

  return data;
}

/**
 * Create a daily check-in and associated conversation
 */
export async function createDailyCheckin(
  userId: string,
  anxietyLevel: number
): Promise<{ checkin: DailyCheckin; conversation: ChatConversation } | null> {
  const supabase = getSupabase();

  // Create the conversation first
  const conversation = await createConversation(userId, 'daily_checkin', {
    title: 'Daily Check-in',
    metadata: { anxiety_level: anxietyLevel, checkin_date: new Date().toISOString() },
  });

  if (!conversation) {
    return null;
  }

  // Create the check-in record using existing table structure
  const { data: checkin, error } = await supabase
    .from('daily_checkins')
    .insert({
      user_id: userId,
      feeling_level: anxietyLevel, // Use existing column name
      conversation_id: conversation.id,
      note: 'Daily check-in',
      current_focus: null, // Will be set by AI or user later
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating daily check-in:', error);
    return null;
  }

  // Send support message if anxiety is high (8+)
  if (anxietyLevel >= 8) {
    try {
      await fetch('/.netlify/functions/send-companion-message', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          eventType: 'high_anxiety',
          metadata: { anxietyLevel }
        })
      });
    } catch (msgError) {
      console.error('Error sending high anxiety message:', msgError);
      // Don't block check-in if message fails
    }
  }

  return { checkin, conversation };
}

/**
 * Send a message in a conversation
 */
export async function sendMessage(
  conversationId: string,
  content: string,
  role: 'user' | 'assistant' = 'user'
): Promise<ChatMessage | null> {
  const supabase = getSupabase();

  const { data, error } = await supabase
    .from('chat_messages')
    .insert({
      conversation_id: conversationId,
      role,
      content,
    })
    .select()
    .single();

  if (error) {
    console.error('Error sending message:', error);
    return null;
  }

  return data;
}

/**
 * Get conversation messages
 */
export async function getConversationMessages(conversationId: string): Promise<ChatMessage[]> {
  const supabase = getSupabase();

  const { data, error } = await supabase
    .from('chat_messages')
    .select('*')
    .eq('conversation_id', conversationId)
    .order('created_at', { ascending: true });

  if (error) {
    console.error('Error fetching messages:', error);
    return [];
  }

  return data || [];
}

/**
 * Get all conversations for a user
 */
export async function getUserConversations(
  userId: string,
  options: {
    type?: 'general' | 'featured' | 'quest' | 'daily_checkin';
    limit?: number;
  } = {}
): Promise<ChatConversation[]> {
  const supabase = getSupabase();

  let query = supabase
    .from('chat_conversations')
    .select('*')
    .eq('user_id', userId)
    .order('updated_at', { ascending: false });

  if (options.type) {
    query = query.eq('conversation_type', options.type);
  }

  if (options.limit) {
    query = query.limit(options.limit);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching conversations:', error);
    return [];
  }

  return data || [];
}

/**
 * Search conversations by content
 */
export async function searchConversations(
  userId: string,
  searchTerm: string
): Promise<ChatConversation[]> {
  const supabase = getSupabase();

  // Search in both conversation titles and message content
  const { data: conversationsWithMessages, error } = await supabase
    .from('chat_messages')
    .select('conversation_id, chat_conversations!inner(*)')
    .eq('chat_conversations.user_id', userId)
    .ilike('content', `%${searchTerm}%`);

  if (error) {
    console.error('Error searching conversations:', error);
    return [];
  }

  // Also search in titles
  const { data: conversationsWithTitles } = await supabase
    .from('chat_conversations')
    .select('*')
    .eq('user_id', userId)
    .ilike('title', `%${searchTerm}%`);

  // Combine and deduplicate results
  const allConversations = new Map<string, ChatConversation>();

  conversationsWithMessages?.forEach((item: any) => {
    const conv = item.chat_conversations;
    allConversations.set(conv.id, conv);
  });

  conversationsWithTitles?.forEach((conv) => {
    allConversations.set(conv.id, conv);
  });

  return Array.from(allConversations.values());
}

/**
 * Call the AI chat API
 */
export async function callAI(
  conversationId: string,
  message: string,
  context?: {
    type?: string;
    trigger?: string;
    current_focus?: string;
    current_anxiety_level?: number;
  }
): Promise<{ reply: string; messageId: string | null } | null> {
  const supabase = getSupabase();

  // Get the current session token
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    console.error('No active session');
    return null;
  }

  // Save user message first
  await sendMessage(conversationId, message, 'user');

  try {
    const response = await fetch('/.netlify/functions/ai-chat', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        conversation_id: conversationId,
        message,
        context,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      console.error('AI API error:', error);
      return null;
    }

    const data = await response.json();
    return {
      reply: data.reply,
      messageId: data.messageId,
    };
  } catch (error) {
    console.error('Error calling AI:', error);
    return null;
  }
}

/**
 * Update conversation title
 */
export async function updateConversationTitle(
  conversationId: string,
  title: string
): Promise<boolean> {
  const supabase = getSupabase();

  const { error } = await supabase
    .from('chat_conversations')
    .update({ title })
    .eq('id', conversationId);

  if (error) {
    console.error('Error updating conversation title:', error);
    return false;
  }

  return true;
}

/**
 * Delete a conversation
 */
export async function deleteConversation(conversationId: string): Promise<boolean> {
  const supabase = getSupabase();

  const { error } = await supabase
    .from('chat_conversations')
    .delete()
    .eq('id', conversationId);

  if (error) {
    console.error('Error deleting conversation:', error);
    return false;
  }

  return true;
}
